import React, { memo } from 'react';
import { DatePicker, Drawer, Button, Table, Input, InputNumber, Select } from 'antd';
import { Store, Plus, Trash2 } from 'lucide-react';
import dayjs from 'dayjs';

import StoreSelectionModal from './StoreSelectionModal';
import { FormField, IconAction } from '../../common';
import { useCampaignForm } from './campaignElements.controller';
import { formatCurrency } from '../../../utils/format';

const CAMPAIGN_TYPES = [
    { label: 'POP blando', value: 'pop_blando' },
    { label: 'POP duro', value: 'pop_duro' }
];

const CHANNELS = [
    { label: 'Tradicional', value: 'tradicional' },
    { label: 'Moderno', value: 'moderno' },
    { label: 'Ambos', value: 'both' }
];

const StoreColumns = [
    {
        title: 'Nombre',
        dataIndex: 'name',
        key: 'name',
        render: (text) => <span className="text-[13px] text-surface-900">{text}</span>
    },
    {
        title: 'Cliente',
        dataIndex: 'clientName',
        key: 'clientName',
        render: (text) => <span className="text-[13px] text-surface-600">{text}</span>
    },
    {
        title: 'Tipo de PDV',
        dataIndex: 'storeType',
        key: 'storeType',
        render: (text) => <span className="text-[13px] text-surface-600">{text}</span>
    }
];

const CampaignDrawer = memo(({ isOpen, setIsOpen, createCampaign, updateCampaign, contextHolder, stores, editMode = false, campaignData = null }) => {
    const form = useCampaignForm({ isOpen, setIsOpen, createCampaign, updateCampaign, stores, editMode, campaignData });

    return (
        <Drawer
            title={editMode ? 'Editar Campaña' : 'Crear Campaña'}
            onClose={form.onCloseDrawer}
            open={isOpen}
            width={600}
            footer={
                <div className="flex justify-end gap-2">
                    <Button onClick={form.onCloseDrawer}>Cancelar</Button>
                    <Button type="primary" onClick={form.onSubmit} disabled={form.loading} loading={form.loading}>
                        {editMode ? 'Actualizar Campaña' : 'Crear Campaña'}
                    </Button>
                </div>
            }>
            {contextHolder}
            <div className="flex flex-col gap-5">
                <FormField label="Nombre de la campaña" required>
                    <Input value={form.name} onChange={(e) => form.setName(e.target.value)} allowClear />
                </FormField>

                <FormField label="Seleccione el tipo de campaña" required>
                    <Select value={form.campaignType} options={CAMPAIGN_TYPES} onChange={(e) => form.setCampaignType(e)} />
                </FormField>

                <FormField
                    label="Precio por piso de venta (USD)"
                    hint="Se aplica a cada piso de venta. Podrás ajustar el precio de un piso puntual desde el detalle de la campaña.">
                    <InputNumber
                        value={form.price}
                        onChange={(value) => form.setPrice(value ?? 0)}
                        min={0}
                        step={1}
                        precision={2}
                        prefix="$"
                        className="w-full"
                    />
                </FormField>

                <div className="grid gap-5 sm:grid-cols-2">
                    <FormField label="Seleccione la fecha de inicio">
                        <DatePicker
                            value={form.startDate ? dayjs(form.startDate) : null}
                            onChange={(e) => form.setStartDate(e ? e.format('YYYY-MM-DD') : '')}
                            className="w-full"
                        />
                    </FormField>
                    <FormField label="Seleccione la fecha de vencimiento" required>
                        <DatePicker
                            minDate={form.today}
                            value={form.expirationDate ? dayjs(form.expirationDate) : null}
                            onChange={(e) => form.setExpirationDate(e ? e.format('YYYY-MM-DD') : '')}
                            className="w-full"
                        />
                    </FormField>
                </div>

                {/* Materials & channels */}
                <div className="flex flex-col gap-3 pt-5 border-t border-surface-100">
                    <div>
                        <div className="text-sm font-semibold text-surface-900">Materiales</div>
                        <p className="m-0 text-xs text-surface-500">Selecciona los materiales y canales:</p>
                    </div>
                    {form.content?.map((item, index) => (
                        <div key={index} className="grid grid-cols-[1fr_160px_32px] items-end gap-3">
                            <FormField label="Material" required>
                                <Input
                                    value={form.content[index].material}
                                    onChange={(e) => form.handleContentChange(index, 'material', e.target.value)}
                                    allowClear
                                />
                            </FormField>
                            <FormField label="Canal" required>
                                <Select value={form.content[index].channel} options={CHANNELS} onChange={(e) => form.handleContentChange(index, 'channel', e)} />
                            </FormField>
                            <div className="flex h-8 items-center justify-center">
                                {index + 1 !== form.content.length && (
                                    <IconAction icon={<Trash2 />} label="Quitar material" danger onClick={() => form.removeContentField(index)} />
                                )}
                                {index + 1 === form.content.length && <IconAction icon={<Plus />} label="Agregar material" onClick={form.addContentField} />}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Stores */}
                <div className="flex flex-col gap-3 pt-5 border-t border-surface-100">
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <div className="text-sm font-semibold text-surface-900">Pisos de venta</div>
                            <div className="text-xs text-surface-500 tabular-nums">{form.selectedStores.length} seleccionados</div>
                        </div>
                        <Button icon={<Store />} onClick={() => form.setIsStoreModalOpen(true)}>
                            Seleccionar Pisos de Venta
                        </Button>
                    </div>

                    {form.selectedStores.length > 0 && (
                        <>
                            <div className="rounded-lg border border-surface-200 overflow-hidden">
                                <Table
                                    dataSource={form.filteredStores}
                                    columns={StoreColumns}
                                    rowKey="_id"
                                    size="small"
                                    scroll={{ y: 300 }}
                                    pagination={false}
                                />
                            </div>
                            <div className="flex items-center justify-between rounded-lg border border-highlight-border bg-highlight-bg px-4 py-3">
                                <span className="text-[13px] text-surface-600">Inversión estimada ({form.selectedStores.length} pisos)</span>
                                <span className="text-sm font-semibold text-surface-900 tabular-nums">{formatCurrency(form.estimatedInvestment)}</span>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <StoreSelectionModal
                isOpen={form.isStoreModalOpen}
                setIsOpen={form.setIsStoreModalOpen}
                setSelectedStores={form.setSelectedStores}
                selectedStores={form.selectedStores}
                stores={stores}
            />
        </Drawer>
    );
});

export default CampaignDrawer;
